import { Box, Button, Card, Typography } from '@mui/material';
import JourneyStatusIcon from '~/components/StandardJourney/JourneyStatusIcon';
import { radius } from '~/styles/tokens/shape';
import { formatCardLabel } from '~/utils/creditCard';
import type { CardChangeContract } from '~/services/modules/cardChange';
import { CARD_CHANGE_APP_IDS } from '../cardChangeAppIds';
import type { CardChangeChangedCard, CardChangeOutcome } from '../useCardChangeJourney';

type Props = {
  outcome: CardChangeOutcome;
  /** Cartão que passou a valer, para «A próxima cobrança vem no …». */
  card: CardChangeChangedCard | null;
  /** Recusa do gateway ou mensagem da API (4xx); sem ela, só o texto padrão. */
  errorMessage: string | null;
  /** No boleto não há «cartão atual»: a falha diz que a assinatura segue no boleto. */
  paymentMethod?: CardChangeContract['payment_method'];
  onRetry: () => void;
  onUseAnotherCard: () => void;
};

const cardSx = {
  alignItems: 'center',
  borderRadius: `${radius.large}px`,
  boxShadow: '0px 1px 1px rgba(0,0,0,0.3), 0px 2px 3px rgba(0,0,0,0.15)',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  p: 3,
  textAlign: 'center',
  width: '100%'
} as const;

/**
 * Desfecho da troca de cartão 2026, sem «Voltar»: cartão trocado ou falha com nova tentativa. «Tentar novamente»
 * passa pela tela «Trocando o cartão…» (`processing`), então este componente não tem estado de carregamento.
 */
export default function CardChangeOutcomeStep({
  outcome,
  card,
  errorMessage,
  paymentMethod,
  onRetry,
  onUseAnotherCard
}: Props) {
  if (outcome === 'success') {
    return (
      <Box
        data-app-id={CARD_CHANGE_APP_IDS.outcome}
        data-outcome="success"
        sx={{ maxWidth: 560, mx: 'auto', width: '100%' }}
      >
        <Card elevation={0} sx={cardSx}>
          <JourneyStatusIcon kind="success" />
          <Typography component="h1" variant="h5">
            Cartão alterado com sucesso!
          </Typography>
          <Typography variant="subtitle1">
            {card
              ? `A próxima cobrança da sua assinatura vem no ${formatCardLabel(card.brand, card.lastFour)}.`
              : 'A próxima cobrança da sua assinatura vem no novo cartão.'}
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      data-app-id={CARD_CHANGE_APP_IDS.outcome}
      data-outcome="failure"
      sx={{ maxWidth: 560, mx: 'auto', width: '100%' }}
    >
      <Card elevation={0} sx={cardSx}>
        <JourneyStatusIcon kind="error" />
        <Typography component="h1" variant="h5">
          Não foi possível trocar o cartão
        </Typography>
        {errorMessage ? (
          <Typography
            data-app-id={`${CARD_CHANGE_APP_IDS.outcome}-api-message`}
            sx={{ fontWeight: 600 }}
            variant="body1"
          >
            {errorMessage}
          </Typography>
        ) : null}
        <Typography variant="body1">
          {paymentMethod === 'boleto'
            ? 'Sua assinatura continua no boleto. Tente novamente em alguns instantes ou use outro cartão. Se o problema continuar, fale com o nosso atendimento.'
            : 'O cartão atual continua valendo para a assinatura. Tente novamente em alguns instantes ou use outro cartão. Se o problema continuar, fale com o seu banco ou com o nosso atendimento.'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Button
            data-app-id={`${CARD_CHANGE_APP_IDS.outcome}-retry`}
            fullWidth
            onClick={onRetry}
            size="large"
            sx={{ borderRadius: `${radius.large}px` }}
            variant="contained"
          >
            Tentar novamente
          </Button>
          <Button
            data-app-id={`${CARD_CHANGE_APP_IDS.outcome}-another-card`}
            fullWidth
            onClick={onUseAnotherCard}
            size="large"
            sx={(theme) => ({
              borderColor: theme.customColors.tertiaryMain,
              borderRadius: `${radius.large}px`,
              color: theme.customColors.tertiaryMain
            })}
            variant="outlined"
          >
            Usar outro cartão
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
